import { useCallback, useEffect, useState } from 'react'
import { AppLayout } from '../components/AppLayout'
import { MediaUploader } from '../components/MediaUploader'
import { InventoryConnectCard } from '../components/supplier/InventoryConnectCard'
import { api } from '../api/client'
import { useApp } from '../store'
import { toast } from '../lib/toast'

/**
 * /seller/mystock — supplier-facing inventory page (Brief J).
 *
 * Lists every stone the signed-in supplier has on Aska, with inline
 * image + video upload slots per stone. Admins see every supplier's
 * stones (server scoping). Pure buyers get redirected because
 * /api/seller/* enforces role=supplier|admin and we surface the 403.
 */
interface StoneRow {
  uniqueId: string
  refStockId: string | null
  tagNo: string
  carat: number
  shape: string | null
  color: string | null
  clarity: string | null
  cut: string | null
  rate: number | null
  certNo: string | null
  lab: string | null
  media: {
    imageUrl: string | null
    videoUrl: string | null
  }
}

export function SellerMyStock() {
  const signedIn = useApp((s) => s.signedIn)
  const [rows, setRows] = useState<StoneRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [showListForm, setShowListForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api<{ stock: StoneRow[]; total: number }>('/seller/mystock')
      setRows(r.stock || [])
      setError(null)
    } catch (e) {
      const err = e as { status?: number; message?: string }
      if (err.status === 403) setError('Supplier or admin access required.')
      else setError(err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])
  // Wait for Firebase auth to restore the persisted session before
  // calling /api/seller/* — otherwise the bearer header is missing on
  // first paint and the API returns 401 with a confusing "Missing
  // Authorization" message.
  useEffect(() => {
    if (signedIn) load()
    else { setLoading(false); setError('Sign in as a supplier to manage media.') }
  }, [signedIn, load])

  const onMediaChange = useCallback((stockId: string, kind: 'image' | 'video', url: string | null) => {
    setRows((prev) => prev.map((r) =>
      r.uniqueId.toUpperCase() === stockId.toUpperCase()
        ? { ...r, media: { ...r.media, [`${kind}Url`]: url } as StoneRow['media'] }
        : r
    ))
  }, [])

  const visible = rows.filter((r) => {
    if (!filter) return true
    const f = filter.toLowerCase()
    return (
      r.uniqueId.toLowerCase().includes(f) ||
      (r.refStockId || '').toLowerCase().includes(f) ||
      (r.shape || '').toLowerCase().includes(f) ||
      (r.color || '').toLowerCase().includes(f)
    )
  })

  return (
    <AppLayout>
      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Supplier · inventory</p>
            <h1 className="text-3xl font-bold text-ink mt-2">Your stones on DiaEx</h1>
            <p className="text-sm text-muted mt-1">
              Add or replace the photo + video buyers see when they look at your stock.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <input
              type="text"
              placeholder="Filter by stock id, ref, shape, color…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-border rounded-md px-3 py-2 text-sm flex-1 md:w-72"
              data-testid="mystock-filter"
            />
            <button
              type="button"
              onClick={() => setShowListForm((v) => !v)}
              className="inline-flex shrink-0 items-center gap-1.5 h-[38px] rounded-full bg-navy px-4 text-sm font-semibold text-white shadow-card hover:bg-navy/90 active:scale-[0.98] transition-all"
              data-testid="mystock-list-toggle"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              List a stone
            </button>
          </div>
        </div>

        {/* Data-source setup skipped at signup ("do it later") lives here. */}
        <InventoryConnectCard enabled={signedIn && !error} />

        {showListForm && (
          <ListStoneForm
            onCreated={() => { setShowListForm(false); void load() }}
            onCancel={() => setShowListForm(false)}
          />
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse h-72" />
            ))}
          </div>
        ) : error ? (
          <p className="text-sm text-danger">{error}</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted">No stones match. Add inventory via your account manager or the Excel upload on aska.co.in.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visible.map((r) => (
              <article key={r.uniqueId} className="card" data-testid="mystock-card" data-stock-id={r.uniqueId}>
                <header className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-soft">
                      {r.refStockId || `#${r.tagNo}`}
                    </p>
                    <p className="text-sm font-bold text-ink">{r.uniqueId}</p>
                    <p className="text-[11px] text-soft mt-1">
                      {[r.shape, r.color, r.clarity, r.cut].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-soft">{Number(r.carat || 0).toFixed(2)} ct</p>
                    {r.rate != null && (
                      <p className="text-xs text-soft">${Number(r.rate).toFixed(2)}/ct</p>
                    )}
                  </div>
                </header>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <MediaUploader
                    stockId={r.uniqueId}
                    kind="image"
                    currentUrl={r.media.imageUrl}
                    onChange={(url) => onMediaChange(r.uniqueId, 'image', url)}
                  />
                  <MediaUploader
                    stockId={r.uniqueId}
                    kind="video"
                    currentUrl={r.media.videoUrl}
                    onChange={(url) => onMediaChange(r.uniqueId, 'video', url)}
                  />
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </AppLayout>
  )
}

/**
 * Manual single-stone add (#39). Suppliers without a feed integration type a
 * stone in directly; it POSTs to /seller/list-stone and shows up in the buyer
 * catalog within the minute. Required: reference, shape, carat, $/ct.
 */
function ListStoneForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ stoneType: 'CVD' })
  const [busy, setBusy] = useState(false)
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((p) => ({ ...p, [k]: e.target.value }))

  const canSubmit = f.refStockId?.trim() && f.shape?.trim() && Number(f.carat) > 0 && Number(f.pricePerCarat) > 0

  async function submit() {
    if (!canSubmit || busy) return
    setBusy(true)
    try {
      const num = (k: string) => (f[k] ? Number(f[k]) : undefined)
      const strOrUndef = (k: string) => (f[k]?.trim() ? f[k].trim() : undefined)
      await api('/seller/list-stone', {
        method: 'POST',
        body: {
          refStockId: f.refStockId.trim(),
          shape: f.shape.trim(),
          carat: Number(f.carat),
          pricePerCarat: Number(f.pricePerCarat),
          color: strOrUndef('color'), clarity: strOrUndef('clarity'), cut: strOrUndef('cut'),
          polish: strOrUndef('polish'), symmetry: strOrUndef('symmetry'), fluorescence: strOrUndef('fluorescence'),
          lab: strOrUndef('lab'), certNo: strOrUndef('certNo'), stoneType: strOrUndef('stoneType'),
          imageUrl: strOrUndef('imageUrl'), videoUrl: strOrUndef('videoUrl'),
          length: num('length'), width: num('width'), height: num('height'),
        },
      })
      toast(`Listed ${f.refStockId.trim().toUpperCase()} — live for buyers shortly`, 'success')
      onCreated()
    } catch (e) {
      const err = e as { status?: number }
      toast(err.status === 409 ? 'A stone with this reference already exists.' : 'Could not list the stone — check the fields and try again.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const input = 'h-10 w-full rounded-lg border border-border bg-paper px-3 text-sm text-ink placeholder:text-soft focus:border-gold focus:outline-none transition-colors'
  const Field = ({ k, label, type = 'text', placeholder, required = false }: { k: string; label: string; type?: string; placeholder?: string; required?: boolean }) => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-soft">{label}{required && <span className="text-gold"> *</span>}</span>
      <input type={type} step={type === 'number' ? 'any' : undefined} value={f[k] ?? ''} onChange={set(k)} placeholder={placeholder} className={input} data-testid={`list-${k}`} />
    </label>
  )

  return (
    <div className="mb-8 rounded-card border border-gold/40 bg-cream/60 p-5 shadow-card" data-testid="list-stone-form">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-ink">List a stone</h2>
        <button type="button" onClick={onCancel} className="text-[11px] font-semibold uppercase tracking-wide text-soft hover:text-ink">Cancel</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Field k="refStockId" label="Reference / cert no" placeholder="e.g. LG12345" required />
        <Field k="shape" label="Shape" placeholder="Round" required />
        <Field k="carat" label="Carat" type="number" placeholder="1.02" required />
        <Field k="pricePerCarat" label="$/carat" type="number" placeholder="4200" required />
        <Field k="color" label="Color" placeholder="D" />
        <Field k="clarity" label="Clarity" placeholder="VS1" />
        <Field k="cut" label="Cut" placeholder="Excellent" />
        <Field k="polish" label="Polish" placeholder="Excellent" />
        <Field k="symmetry" label="Symmetry" placeholder="Excellent" />
        <Field k="fluorescence" label="Fluorescence" placeholder="None" />
        <Field k="lab" label="Lab" placeholder="IGI" />
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-soft">Growth</span>
          <select value={f.stoneType ?? 'CVD'} onChange={set('stoneType')} className={input} data-testid="list-stoneType">
            <option>CVD</option><option>HPHT</option><option>Natural</option>
          </select>
        </label>
        <Field k="length" label="Length (mm)" type="number" placeholder="6.5" />
        <Field k="width" label="Width (mm)" type="number" placeholder="4.3" />
        <Field k="height" label="Depth (mm)" type="number" placeholder="2.6" />
        <Field k="certNo" label="Cert no (if different)" placeholder="optional" />
        <Field k="imageUrl" label="Image URL" placeholder="https://…" />
        <Field k="videoUrl" label="Video URL" placeholder="https://…" />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit || busy}
          className="inline-flex h-11 items-center justify-center rounded-full bg-navy px-6 text-sm font-semibold text-white shadow-card hover:bg-navy/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="list-stone-submit"
        >
          {busy ? 'Listing…' : 'List this stone'}
        </button>
        <p className="text-[11px] text-soft">Required: reference, shape, carat, $/ct. Buyers see it within a minute.</p>
      </div>
    </div>
  )
}
